import React from 'react';

interface AIInsightsTabProps {
  athleteData: any;
  isDark: boolean;
}

const AIInsightsTab: React.FC<AIInsightsTabProps> = ({ athleteData, isDark }) => {
  const [activeMode, setActiveMode] = React.useState<'performance' | 'training'>('performance');
  
  const perfSummaryBullets: string[] = [
    `Readiness today: ${athleteData?.ai_insights?.readiness_level ?? 'N/A'}`,
    `Latest recovery score: ${athleteData?.recovery?.stats?.latest_recovery ?? 'N/A'}`,
    `Resting HR (latest): ${athleteData?.recovery?.recovery_data?.slice?.(-1)?.[0]?.resting_heart_rate ?? 'N/A'} bpm`,
    `HRV RMSSD (latest): ${athleteData?.recovery?.recovery_data?.slice?.(-1)?.[0]?.hrv_rmssd_milli ?? 'N/A'} ms`,
    `Sleep duration last night: ${athleteData?.sleep?.stats?.latest_duration ?? 'N/A'} hrs`,
    `Average strain last 7 days: ${athleteData?.activity?.stats?.average_strain ?? 'N/A'}`,
  ];
  const trainSummaryBullets: string[] = [
    `Macrocycle focus: ${athleteData?.ai_insights?.macrocycle_focus ?? 'Base/Build'}`,
    `Last 4 weeks avg strain: ${athleteData?.activity?.stats?.total_strain ?? 'N/A'}`,
    `Sleep efficiency trend: ${athleteData?.sleep?.stats?.average_quality ?? 'N/A'}`,
    `HRV trend: ${athleteData?.recovery?.stats?.recovery_trend ?? 'N/A'}`,
  ];

  const sectionCard = (title: string, items: string[], icon?: string) => (
    <div className={`${isDark ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-lg rounded-2xl p-6 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
      <h4 className="text-xl font-bold theme-text-primary mb-4 flex items-center space-x-2">
        {icon && <span>{icon}</span>}
        <span>{title}</span>
      </h4>
      <ul className="space-y-3 list-disc pl-6">
        {items.map((it, i) => (
          <li key={i} className="theme-text-secondary leading-relaxed">{it}</li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Mode Toggle */}
      <div className={`${isDark ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-lg rounded-2xl p-2 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl flex w-full`}> 
        <button
          className={`flex-1 py-2 rounded-xl font-medium transition-colors ${activeMode === 'performance' ? 'bg-blue-600 text-white' : 'theme-text-primary hover:bg-blue-50 dark:hover:bg-gray-700/40'}`}
          onClick={() => setActiveMode('performance')}
        >
          Performance
        </button>
        <button
          className={`flex-1 py-2 rounded-xl font-medium transition-colors ${activeMode === 'training' ? 'bg-emerald-600 text-white' : 'theme-text-primary hover:bg-emerald-50 dark:hover:bg-gray-700/40'}`}
          onClick={() => setActiveMode('training')}
        >
          Training
        </button>
      </div>

      {/* Overall Summary (full width rectangle) */}
      <div className={`${isDark ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-lg rounded-2xl p-6 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}> 
        <h4 className="text-xl font-bold theme-text-primary mb-4 flex items-center space-x-2">
          <span>📋</span>
          <span>{activeMode === 'performance' ? 'Today’s Performance Summary' : 'Training Block Overview'}</span>
        </h4>
        <ul className="space-y-2 list-disc pl-6">
          {(activeMode === 'performance' ? perfSummaryBullets : trainSummaryBullets).map((b, i) => (
            <li key={i} className="theme-text-secondary leading-relaxed">{b}</li>
          ))}
        </ul>
      </div>

      {/* AI Insights Content */}
      {athleteData.ai_insights.error ? (
        <div className="bg-red-50 dark:bg-red-900/20 rounded-2xl p-6 border border-red-200/50 dark:border-red-700/50">
          <h4 className={`text-lg font-semibold ${isDark ? 'text-red-400' : 'text-red-600'} mb-2`}>AI Analysis Unavailable</h4>
          <p className="theme-text-secondary">{athleteData.ai_insights.error}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {activeMode === 'performance' ? (
            <>
              <div className="lg:col-span-2">
              {sectionCard('Athlete Recommendations', (() => {
                const latestRecovery = athleteData?.recovery?.stats?.latest_recovery as number | undefined;
                const lastSleepHrs = athleteData?.sleep?.stats?.latest_duration as number | undefined;
                const hrv = athleteData?.recovery?.recovery_data?.slice(-1)[0]?.hrv_rmssd_milli as number | undefined;
                const avgStrain = athleteData?.activity?.stats?.average_strain as number | undefined;

                const bullets: string[] = [];
                // Intensity guidance
                if (typeof latestRecovery === 'number') {
                  if (latestRecovery >= 66) {
                    bullets.push('Intensity: High day. Schedule quality work (e.g., threshold/VO2 blocks, fast tempo) with controlled volume.');
                    bullets.push('Strength output: 85–90% of recent bests on primary lifts; hold RPE 7–8 for main sets.');
                  } else if (latestRecovery >= 40) {
                    bullets.push('Intensity: Moderate day. Prioritize technical quality and aerobic intervals; limit time above threshold.');
                    bullets.push('Strength output: 75–85% on primary lifts; RPE 6–7, focus on speed and form.');
                  } else {
                    bullets.push('Intensity: Low day. Keep sessions in Zone 1–2 with mobility and activation; avoid heavy neuromuscular load.');
                    bullets.push('Strength output: 60–70% technique work only; RPE ≤ 6, terminate sets at first sign of form decay.');
                  }
                } else {
                  bullets.push('Intensity: Base on readiness. If unsure, start moderate and adjust by RPE and movement quality.');
                }

                // Diet guidance
                if (typeof latestRecovery === 'number' && latestRecovery >= 66) {
                  bullets.push('Nutrition: Higher carbohydrate availability pre-session (2–3 g/kg/day) and 30–60g carbs per training hour; lean protein at meals.');
                } else if (typeof latestRecovery === 'number' && latestRecovery < 40) {
                  bullets.push('Nutrition: Emphasize anti-inflammatory foods (omega-3s, berries, leafy greens) and adequate protein (1.6–2.2 g/kg/day).');
                } else {
                  bullets.push('Nutrition: Balanced intake; ensure pre-training carbs and post-training protein within 60 minutes. Hydration > 30 ml/kg/day.');
                }

                // Sleep and recovery context
                if (typeof lastSleepHrs === 'number') {
                  if (lastSleepHrs < 7) bullets.push('Sleep note: Last night sub-7h; extend warm-up by 10–15 minutes and cap intensity spikes.');
                  else bullets.push('Sleep note: Adequate sleep; proceed with planned warm-up and main set progressions.');
                }

                // HRV & strain nuance
                if (typeof hrv === 'number' && typeof avgStrain === 'number') {
                  if (hrv < 25 && avgStrain > 12) bullets.push('HRV/Strain: Autonomic stress high. Favor aerobic maintenance and technique; postpone maximal outputs.');
                  else bullets.push('HRV/Strain: Tolerable. Include brief high-quality efforts with full recoveries to drive adaptation.');
                }

                // Push guidance
                if (typeof latestRecovery === 'number') {
                  if (latestRecovery >= 66) bullets.push('Push guidance: Progress load only if bar speed and mechanics are stable across sets.');
                  else if (latestRecovery < 40) bullets.push('Push guidance: Keep reserve (≥3 reps in tank); end sets at the first technical breakdown.');
                }

                return bullets;
              })(), '🧠')}
                  </div>
              {sectionCard('Game-Day Readiness', (() => {
                const latestRecovery = athleteData?.recovery?.stats?.latest_recovery as number | undefined;
                const rhr = athleteData?.recovery?.recovery_data?.slice?.(-1)?.[0]?.resting_heart_rate as number | undefined;
                const hrv = athleteData?.recovery?.recovery_data?.slice?.(-1)?.[0]?.hrv_rmssd_milli as number | undefined;
                const bullets: string[] = [];
                if (typeof latestRecovery === 'number') {
                  if (latestRecovery >= 66) bullets.push('You are primed for quality work. Expect good tolerance for high-intensity sets and crisp movement mechanics.');
                  else if (latestRecovery >= 40) bullets.push('You can train productively, but cap the number of high-intensity bouts and prioritize technical precision.');
                  else bullets.push('Body is signaling low readiness. Keep intensity low; focus on movement quality, mobility, and aerobic maintenance.');
                } else {
                  bullets.push('Readiness unclear. Start conservatively and adjust by perceived effort and movement quality.');
                }
                if (typeof rhr === 'number') {
                  if (rhr > 5 + (athleteData?.ai_insights?.rhr_baseline ?? rhr)) bullets.push('Elevated resting HR suggests residual fatigue or stress; extend warm-up and avoid maximal efforts.');
                  else bullets.push('Resting HR near baseline; cardiac readiness is adequate for planned work.');
                }
                if (typeof hrv === 'number') {
                  if (hrv < 25) bullets.push('Lower HRV indicates autonomic strain—prefer consistent pacing and generous recoveries between sets.');
                  else bullets.push('HRV is supportive of higher-quality intervals; use full recoveries to keep power output consistent.');
                }
                if (bullets.length === 0) bullets.push('No readiness suggestions available yet.');
                return bullets;
              })(), '🧭')}
              {sectionCard('Sleep Impact', (() => {
                const lastHrs = athleteData?.sleep?.stats?.latest_duration as number | undefined;
                const avgQual = athleteData?.sleep?.stats?.average_quality as number | undefined;
                const eff = athleteData?.sleep?.sleep_data?.slice?.(-1)?.[0]?.sleep_efficiency as number | undefined;
                const bullets: string[] = [];
                if (typeof lastHrs === 'number') {
                  if (lastHrs < 7) bullets.push('Sub-7h sleep: reduce sprint or plyometric dose; extend warm-up 10–15 minutes to restore coordination.');
                  else bullets.push('Adequate sleep: proceed with planned progressions; maintain high technical standards.');
                }
                if (typeof avgQual === 'number') {
                  if (avgQual < 70) bullets.push('Recent sleep quality is suppressed—expect slower recovery between sets; keep rest intervals generous.');
                  else bullets.push('Sleep quality supports quality training; use structured intervals to maximize adaptation.');
                }
                if (typeof eff === 'number') {
                  if (eff < 85) bullets.push('Sleep efficiency is low—deprioritize maximal power output; emphasize rhythm and tempo work.');
                }
                if (bullets.length === 0) bullets.push('No sleep-based adjustments at this time.');
                return bullets;
              })(), '😴')}
              {sectionCard('Load & Strain', (() => {
                const avgStrain = athleteData?.activity?.stats?.average_strain as number | undefined;
                const totalStrain = athleteData?.activity?.stats?.total_strain as number | undefined;
                const hours = athleteData?.activity?.stats?.total_duration_hours as number | undefined;
                const bullets: string[] = [];
                if (typeof avgStrain === 'number') {
                  if (avgStrain >= 12) bullets.push('Recent load is high—make today a quality-over-quantity session; avoid stacking multiple hard modules.');
                  else if (avgStrain >= 8) bullets.push('Moderate load—include one focused high-intensity block, then transition to technique or aerobic support.');
                  else bullets.push('Lower recent load—opportunity to insert a controlled progression, monitoring form and bar/pace velocity.');
                }
                if (typeof hours === 'number' && hours > 8) bullets.push('Large weekly duration—guard against cumulative fatigue by trimming accessories and adding cooldown work.');
                if (typeof totalStrain === 'number' && totalStrain > 90) bullets.push('Total strain elevated—aim for steadier outputs with full recoveries to consolidate adaptations.');
                if (bullets.length === 0) bullets.push('No load adjustments recommended based on current records.');
                return bullets;
              })(), '📈')}
              {sectionCard('Immediate Priorities', (
                athleteData?.ai_insights?.next_week_focus && athleteData.ai_insights.next_week_focus.length > 0
                  ? athleteData.ai_insights.next_week_focus.slice(0, 3).map((x: string, i: number) => `${i + 1}. ${x}`)
                  : ['No immediate priorities available.']
              ), '🎯')}
            </>
          ) : (
            <>
              <div className="lg:col-span-2">
              {sectionCard('Suggested Workload', (() => {
                const weekly = athleteData?.ai_insights?.weekly_sessions ?? '5';
                const keySessions = athleteData?.ai_insights?.key_sessions?.join(', ') ?? 'Primary strength, Aerobic threshold, Skill/technique';
                const modality = athleteData?.ai_insights?.recovery_modality ?? 'Mobility, Sauna, Easy Swim';
                const hrvTrend = athleteData?.recovery?.stats?.recovery_trend as number | undefined;

                const bullets: string[] = [];
                bullets.push(`Plan: ${weekly} sessions this week, distributed across high/medium/low to manage fatigue and maximize adaptation.`);
                bullets.push(`Key sessions: ${keySessions}. These target the athlete’s limiting factors and maintain strengths.`);
                bullets.push(`Recovery modalities: ${modality}. Schedule on low days and post-intense sessions to speed autonomic recovery.`);
                if (typeof hrvTrend === 'number') {
                  if (hrvTrend < 0) bullets.push('Rationale: HRV trending down—reduce cumulative intensity by ~10–15% and prioritize aerobic base + technique.');
                  else bullets.push('Rationale: HRV stable/improving—progress main lifts by 2–5% and extend threshold intervals cautiously.');
                }
                bullets.push('Nutrition: Aim 1.6–2.2 g/kg/day protein; time carbs around key sessions (1–1.2 g/kg within 2h post).');
                bullets.push('Strength output targets: Main lifts at 80–88% 1RM with bar speed monitoring; deload if velocity loss >20%.');
                bullets.push('Push guidance: Use RPE 7–8 on key days, RPE 5–6 on support days to preserve quality for progression.');
                return bullets;
              })(), '🛠️')}
              </div>
              {sectionCard('Block Objectives', (() => {
                const macro = athleteData?.ai_insights?.macrocycle_focus ?? 'Base/Build';
                const meso = athleteData?.ai_insights?.mesocycle_week ?? '1';
                const micro = athleteData?.ai_insights?.microcycle_focus ?? 'Technique + Aerobic';
                const items = [
                  `Macrocycle focus “${macro}”: prioritize adaptations aligned to this phase; de-emphasize conflicting qualities.`,
                  `Mesocycle week ${meso}: adjust weekly peaks/deloads accordingly to manage fatigue and stimulus.`,
                  `Microcycle emphasis “${micro}”: ensure daily sessions include targeted technical or energy system work.`,
                ];
                return items.length ? items : ['No block objectives available.'];
              })(), '📅')}
              {sectionCard('Capacity & Readiness Trends', (() => {
                const hrvTrend = athleteData?.recovery?.stats?.recovery_trend as number | undefined;
                const sleepAvg = athleteData?.sleep?.stats?.average_duration as number | undefined;
                const strain28 = athleteData?.activity?.stats?.total_strain as number | undefined;
                const bullets: string[] = [];
                if (typeof hrvTrend === 'number') {
                  if (hrvTrend < 0) bullets.push('HRV trending down: bias training toward aerobic base and technique; reduce cumulative intensity 10–15%.');
                  else bullets.push('HRV stable/improving: cautiously progress main lifts and threshold duration; monitor recovery markers daily.');
                }
                if (typeof sleepAvg === 'number') {
                  if (sleepAvg < 7) bullets.push('Monthly sleep average under 7h: elevate recovery blocks and reduce late-day stimulants; formalize sleep routine.');
                }
                if (typeof strain28 === 'number') {
                  if (strain28 > 350) bullets.push('28-day load is high: schedule strategic deload or technique microcycle to consolidate gains.');
                }
                if (bullets.length === 0) bullets.push('No readiness trends available to guide adjustments.');
                return bullets;
              })(), '📊')}
              {sectionCard('Watch-outs', (
                (athleteData?.ai_insights?.areas_of_concern && athleteData.ai_insights.areas_of_concern.length > 0)
                  ? (athleteData.ai_insights.areas_of_concern as string[])
                  : ['No watch-outs at this time.']
              ), '⚠️')}
              {sectionCard('Immediate Priorities', (
                athleteData?.ai_insights?.next_week_focus && athleteData.ai_insights.next_week_focus.length > 0
                  ? (athleteData.ai_insights.next_week_focus as string[]).slice(0, 3).map((x: string, i: number) => `${i + 1}. ${x}`)
                  : ['No immediate priorities available.']
              ), '🎯')}
            </>
          )}
        </div>
      )}

      {/* Analysis Metadata */}
      {(() => {
        const ts = athleteData?.ai_insights?.generated_at;
        const d = ts ? new Date(ts) : null;
        const isValid = d instanceof Date && !isNaN(d.getTime());
        if (!isValid) return null;
        return (
          <div className={`${isDark ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-lg rounded-2xl p-4 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
            <div className="text-center text-sm theme-text-secondary">
              <span>Analysis generated: {d.toLocaleString()}</span>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default AIInsightsTab; 